import React, { useEffect, useRef, useState } from 'react';
import './ForceStart.css';

// MUI
import { Grid, styled, TextField, Autocomplete, Chip } from '@mui/material';

// Util
import ExistingSessionInfo from './ExistingSessionInfo';
import { send_request } from '../../../../utils/Request';

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  backgroundColor: `${theme.palette.background.paper} !important`,
  minWidth: '50%',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  backgroundColor: `${theme.palette.background.paper} !important`,
  width: '100%%',
}));

const Text = styled('span')(({ textColor }) => ({
  fontSize: '14px',
  color: textColor,
}));

function SelectSessionDropdown({
  projectId,
  StyledSkeleton,
  setValues,
  values,
  placeholders,
  setIsLoading,
  setLoadingInputPHs,
  isTest,
  version,
  selected,
  setSelected,
}) {
  const [sessionIds, setSessionIds] = useState(null);
  //Added this for now. Selected wants to take in Array of strings (sessionIds) but the AutoComplete wants to take in array of objects
  //Made a second hook for now until figure out a better solution

  const [inputValue, setInputValue] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchNotFound, setSearchNotFound] = useState(false);
  const searchDebounceRef = useRef(null);
  const searchRequestIdRef = useRef(0);

  useEffect(() => {
    const loadRecentSessions = async () => {
      // Fetch all the existing session ids linked to the given project id
      let url = `project-service/project-session/get_recent_existing_session_ids/${projectId}`;
      if (isTest) url += '?includeTestSessions=true';

      let json = await send_request(url, null, null, 'GET');
      let dropdownTitleArray = [];
      const responseProjectSessions = json?.data;
      if (!json || !responseProjectSessions) {
        setSessionIds(dropdownTitleArray);
        return;
      } else {
        dropdownTitleArray = responseProjectSessions;
      }
      setSessionIds(dropdownTitleArray);
    };
    loadRecentSessions();
  }, []);

  useEffect(() => {
    if (selected?.length === 1 && selected[0]) {
      handleFetchExisting(selected[0]);
    } else {
      setValues(null);
    }
  }, [selected]);

  // Returns true if the given ID is already known to be a valid session for
  // this project (either in the 50 most-recent results or already in selected).
  const isAlreadyKnownValid = candidate => {
    if (!candidate) return false;
    if (selected.includes(candidate)) return true;
    if ((sessionIds || []).some(s => (s?.sessionId || s) === candidate)) return true;
    return false;
  };

  // Performs the backend existence check for a typed session ID. Returns the
  // confirmed sessionId if the project contains this session, otherwise null.
  // Short-circuits if the ID is already known to be valid.
  const lookupSessionId = async candidate => {
    const trimmed = (candidate || '').trim();
    if (trimmed.length < 8) return null;
    if (isAlreadyKnownValid(trimmed)) return trimmed;

    const url = `project-service/project-session/get_session_version/${encodeURIComponent(
      projectId
    )}/${encodeURIComponent(trimmed)}`;
    const json = await send_request(url, null, null, 'GET');

    if (json && json.data != null) return trimmed;
    return null;
  };

  // Debounced lookup while typing. Adds a "Search result" option to the
  // dropdown when the backend confirms the typed ID exists.
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }

    const trimmed = (inputValue || '').trim();

    // Invalidate any previous search result as soon as the typed value
    // changes. Prevents a stale "Search result" option from remaining
    // clickable while a new lookup is pending.
    if (searchResult && searchResult.sessionId !== trimmed) {
      setSearchResult(null);
    }
    setSearchNotFound(false);

    // Bump the requestId on every effect run so any still-in-flight lookup
    // from a previous run is ignored when its response lands.
    const requestId = ++searchRequestIdRef.current;

    if (trimmed.length < 8 || isAlreadyKnownValid(trimmed)) {
      setSearchResult(null);
      setSearching(false);
      return;
    }

    setSearching(true);

    searchDebounceRef.current = setTimeout(async () => {
      const confirmedId = await lookupSessionId(trimmed);

      if (requestId !== searchRequestIdRef.current) return;

      if (confirmedId) {
        setSearchResult({
          sessionId: confirmedId,
          timeAgo: null,
          createdAt: new Date().toISOString(),
          group: 'Search result',
        });
        setSearchNotFound(false);
      } else {
        setSearchResult(null);
        setSearchNotFound(true);
      }
      setSearching(false);
    }, 400);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
    };
  }, [inputValue, sessionIds, selected, projectId]);

  // On blur, if the user typed a valid session ID but didn't click the
  // dropdown option, run the lookup synchronously and commit it so they
  // don't have to remember to press Enter / click the option explicitly.
  const handleBlur = async () => {
    const trimmed = (inputValue || '').trim();
    if (!trimmed) return;

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    const requestId = ++searchRequestIdRef.current;

    if (isAlreadyKnownValid(trimmed)) {
      if (!selected.includes(trimmed)) {
        setSelected([...selected, trimmed]);
      }
      setInputValue('');
      setSearchResult(null);
      setSearchNotFound(false);
      return;
    }

    setSearching(true);
    const confirmedId = await lookupSessionId(trimmed);

    if (requestId !== searchRequestIdRef.current) return;
    setSearching(false);

    if (confirmedId) {
      setSelected([...new Set([...selected, confirmedId])]);
      setInputValue('');
      setSearchResult(null);
      setSearchNotFound(false);
    } else {
      setSearchResult(null);
      setSearchNotFound(true);
    }
  };

  // Function for fetching existing placeholder data values and setting
  // It into the state
  const handleFetchExisting = async selectedId => {
    if (!selectedId) return;

    setIsLoading(true);
    setLoadingInputPHs(true);

    const url = `project-service/project-session/get_existing_session_placeholder_data/${selectedId}`;
    let json = await send_request(url, placeholders, '', 'POST');

    if (!json || !json.data) {
      setIsLoading(false);
      setLoadingInputPHs(false);
      return;
    }

    // Transform the response data to maintain the correct structure
    const transformedValues = {};
    for (const key in json.data) {
      const placeholder = placeholders.find(p => p.key === key);
      if (placeholder) {
        const placeholderType = placeholder.type;
        const placeholderData = json.data[key];
        transformedValues[key] = {
          type: placeholderType,
          value:
            placeholderType === 'datetime'
              ? {
                  rawValue: placeholderData?.rawValue || null,
                  format: 'yyyy-MM-DD hh:MM:ssZ',
                  value: placeholderData?.value || '',
                }
              : placeholderData,
        };
      }
    }

    setValues(transformedValues);
    setIsLoading(false);
    setLoadingInputPHs(false);

    return transformedValues;
  };

  const handleSelectionChange = (event, newValue) => {
    const newSessionIdList = [
      ...new Set(newValue.filter(session => session).map(session => session.sessionId || session)),
    ];

    setSelected(newSessionIdList);
  };

  const handlePaste = event => {
    const pastedText = event.clipboardData.getData('text');

    if (pastedText.includes(',')) {
      event.preventDefault();

      const pastedIds = pastedText
        .split(',')
        .map(id => id.trim())
        .filter(id => id.length > 0);

      if (pastedIds.length > 0) {
        const combinedIds = [...new Set([...selected, ...pastedIds])];
        setSelected(combinedIds);
      }
    }
  };

  const categorizeByDate = session => {
    const lastUpdated =
      session?.lastUpdated || session?.createdAt || session?.date || session?.timestamp;

    if (!lastUpdated) {
      return 'Other';
    }

    const sessionDate = new Date(lastUpdated);
    if (isNaN(sessionDate.getTime())) {
      return 'Other';
    }

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
    const sessionDateOnly = new Date(
      sessionDate.getFullYear(),
      sessionDate.getMonth(),
      sessionDate.getDate()
    );

    if (sessionDateOnly.getTime() === today.getTime()) {
      return 'Today';
    }

    if (sessionDateOnly.getTime() === yesterday.getTime()) {
      return 'Yesterday';
    }

    return sessionDateOnly.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const availableOptions = sessionIds
    ? sessionIds
        .filter(session => {
          const sessionId = session?.sessionId || session;
          return !selected.includes(sessionId);
        })
        .map(session => ({
          ...session,
          group: categorizeByDate(session),
        }))
    : [];

  const recentSortedOptions = availableOptions.sort((a, b) => {
    const aDate = new Date(a.lastUpdated || a.createdAt || a.date || a.timestamp || 0);
    const bDate = new Date(b.lastUpdated || b.createdAt || b.date || b.timestamp || 0);

    return bDate.getTime() - aDate.getTime();
  });

  const sortedOptions =
    searchResult && !selected.includes(searchResult.sessionId)
      ? [searchResult, ...recentSortedOptions]
      : recentSortedOptions;

  return sessionIds ? (
    <Grid container rowGap={2} marginBottom={1}>
      <Grid item xs={12}>
        <Grid container rowGap={2}>
          <Grid item xs={12}>
            <b>Select session</b>
          </Grid>
          <Grid item xs={12}>
            <StyledAutocomplete
              className="force-start-autocomplete"
              multiple
              disableCloseOnSelect
              variant="standard"
              options={sortedOptions}
              groupBy={option => option.group}
              getOptionLabel={option => {
                if (!option) return '';
                if (typeof option === 'string') return option;
                const id = option?.sessionId ?? '';
                if (!option?.timeAgo) return id;
                return `${id} (${option.timeAgo})`;
              }}
              isOptionEqualToValue={(option, value) => {
                const optId = option?.sessionId || option;
                const valId = value?.sessionId || value;
                return optId === valId;
              }}
              onChange={handleSelectionChange}
              value={selected}
              inputValue={inputValue}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === 'reset') {
                  setInputValue('');
                  return;
                }
                setInputValue(newInputValue);
              }}
              loading={searching}
              loadingText="Searching for session..."
              noOptionsText={
                searchNotFound ? 'No session with that ID exists in this project.' : 'No options'
              }
              renderInput={params => (
                <StyledTextField
                  {...params}
                  size="small"
                  placeholder="Type in or search for session IDs (paste comma-separated IDs)"
                  onPaste={handlePaste}
                  onBlur={handleBlur}
                  error={searchNotFound}
                  helperText={
                    searchNotFound
                      ? 'No session with that ID exists in this project.'
                      : undefined
                  }
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((selectedOption, index) => {
                  return <Chip label={selectedOption} {...getTagProps({ index })} />;
                })
              }
            />
          </Grid>
        </Grid>
      </Grid>
      {selected.length > 0 && (
        <Grid item xs={12}>
          {selected.map(sessionId => (
            <ExistingSessionInfo
              key={sessionId}
              selected={sessionId}
              projectId={projectId}
              version={version}
            />
          ))}
        </Grid>
      )}
    </Grid>
  ) : (
    <StyledSkeleton variant="rectangular" height="35px" />
  );
}

export default SelectSessionDropdown;
